import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from "recharts";
import {
  getL1SheetProperty,
  getL2SheetProperty,
} from "../../../features/reduxworkbook/WorkbookSlice";

const RADIAN = Math.PI / 180;
const iR = 50;
const oR = 100;
const value = 100;

const needle = (value, data, cx, cy, iR, oR, color) => {
  let total = 0;
  data.forEach((v) => {
    total += v.value1;
  });
  const ang = 180.0 * (1 - value / total);
  const length = (iR + 2 * oR) / 3;
  const sin = Math.sin(-RADIAN * ang);
  const cos = Math.cos(-RADIAN * ang);
  const r = 5;
  const x0 = cx;
  const y0 = cy;
  const xba = x0 + r * sin;
  const yba = y0 - r * cos;
  const xbb = x0 - r * sin;
  const ybb = y0 + r * cos;
  const xp = x0 + length * cos;
  const yp = y0 + length * sin;

  return [
    <circle
      key="needle-circle"
      cx={x0}
      cy={y0}
      r={r}
      fill={color}
      stroke="none"
    />,
    <path
      key="needle-path"
      d={`M${xba} ${yba}L${xbb} ${ybb} L${xp} ${yp} L${xba} ${yba}`}
      stroke="none"
      fill={color}
    />,
  ];
};

export default function NeedlePieChart({ Sheetid }) {
  const containerRef = useRef(null);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });

  const conditionalFormattedData = useSelector((state) => {
    const conditionalFormattingArray = getL2SheetProperty(
      state,
      Sheetid,
      "customization",
      "conditionalFormatting"
    );
    return conditionalFormattingArray;
  });

  const [NeedleData, setNeedleData] = useState([]);

  useEffect(() => {
    if (conditionalFormattedData) {
      const formattedData = conditionalFormattedData.flatMap((item) =>
        item.rulesArray.map((rule) => ({
          value1: Number(rule.value1),
          value2: rule.value2 ? Number(rule.value2) : undefined,
          conditions: rule.conditions,
          style: {
            backgroundColor: rule.style.backgroundColor,
            color: rule.style.color,
          },
        }))
      );
      setNeedleData(formattedData);
    }
  }, [conditionalFormattedData]);
  // console.log();

  useEffect(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setContainerDimensions({ width, height });
    }
  }, [containerRef.current]);

  const cx = containerDimensions.width / 2;
  const cy = containerDimensions.height / 2;

  const customizationObject = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "customization")
  );
  const isLegendVisible = customizationObject.isLegendVisible;
  const legendAlign = customizationObject.legendAlign;
  const legendVerticalAlign = customizationObject.legendVerticalAlign;
  const legendLayout = customizationObject.legendLayout;
  const [outerRadius, setOuterRadius] = useState(0);
  useEffect(() => {
    if (containerRef.current) {
      const updateOuterRadius = () => {
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;
        const maxRadius = Math.min(containerWidth, containerHeight) / 2;
        setOuterRadius(Math.min(customizationObject.outerRadius, maxRadius));
      };

      updateOuterRadius();
      window.addEventListener("resize", updateOuterRadius);

      return () => window.removeEventListener("resize", updateOuterRadius);
    }
  }, [customizationObject.outerRadius]);

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", paddingTop: "20px" }}
    >
      <ResponsiveContainer width="100%" height="100%">
        {NeedleData.length && (
          <PieChart>
            <Pie
              dataKey="value1"
              startAngle={180}
              endAngle={0}
              data={NeedleData}
              cx={cx}
              cy={cy}
              innerRadius={
                customizationObject.donut ? customizationObject.innerRadius : 50
              }
              outerRadius={outerRadius <= 80 ? 80 : outerRadius}
              paddingAngle={customizationObject.paddingAngle}
              fill="#8884d8"
              stroke="none"
            >
              {NeedleData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.style.backgroundColor}
                />
              ))}
            </Pie>
            {needle(value, NeedleData, cx, cy, iR, oR, "#d0d000")}
            {isLegendVisible && (
              <Legend
                align={legendAlign.value}
                verticalAlign={legendVerticalAlign.value}
                layout={legendLayout.value}
              />
            )}
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
